import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  Button as MUIButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FiCheck, FiSend, FiUsers } from "react-icons/fi";
import { referralVariants } from "./referralConfig";

/**
 * PostSubmissionSuccessModal
 *
 * Displays the post-submission confirmation and audience-specific referral messaging.
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Dismiss callback to close modal and return to Community page
 * @param {string} [props.sessionStartTime="14:00"] - Formatted session start time (e.g. "14:00")
 * @param {"business-owner" | "virtual-assistant"} [props.audience="business-owner"] - Resolved audience variant
 * @param {string} [props.accountOwnerFirstName] - Account owner first name for VA heading
 * @param {string} [props.firstName] - Current user's first name for business owner heading
 */
export const PostSubmissionSuccessModal = ({
  isOpen,
  onClose,
  sessionStartTime = "14:00",
  audience = "business-owner",
  accountOwnerFirstName,
  firstName,
}) => {
  const variant =
    referralVariants[audience] || referralVariants["business-owner"];

  // Determine dynamic heading based on audience
  const headingText =
    audience === "virtual-assistant"
      ? variant.getHeading(accountOwnerFirstName)
      : variant.getHeading(firstName);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="post-submission-success-title"
      aria-describedby="post-submission-success-desc"
      PaperProps={{
        sx: {
          borderRadius: { xs: "20px", sm: "28px" },
          maxWidth: "460px",
          width: "100%",
          m: 2,
          p: { xs: 2.5, sm: 3.5 },
          position: "relative",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      {/* Top-Right Close Button */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: { xs: 16, sm: 20 },
          top: { xs: 16, sm: 20 },
          color: "#9CA3AF",
          padding: 0.75,
          "&:hover": {
            color: "#374151",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <DialogContent sx={{ p: 0, overflow: "visible" }}>
        {/* Success Icon Badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 1, sm: 1.5 },
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              backgroundColor: "#E6F9EF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiCheck size={28} strokeWidth={2.75} color="#00BA34" />
          </Box>
        </Box>

        {/* Confirmation Heading & Description */}
        <Typography
          id="post-submission-success-title"
          component="h2"
          sx={{
            textAlign: "center",
            fontWeight: 800,
            fontSize: { xs: "1.35rem", sm: "1.55rem" },
            color: "#111827",
            lineHeight: 1.25,
            mb: 1,
            letterSpacing: "-0.01em",
          }}
        >
          Your post is submitted
        </Typography>

        <Typography
          id="post-submission-success-desc"
          sx={{
            textAlign: "center",
            fontSize: { xs: "0.9rem", sm: "0.975rem" },
            color: "#6B7280",
            lineHeight: 1.45,
            mb: 3,
            px: 1,
          }}
        >
          You’re all set for the session starting at {sessionStartTime}.
        </Typography>

        {/* Referral Incentive Card */}
        <Box
          sx={{
            backgroundColor: "#F0F7FE",
            border: "1px solid #D8E8F8",
            borderRadius: "16px",
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {/* White Rounded User Icon Badge */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FiUsers size={22} color="#0085FF" />
            </Box>

            {/* Referral Content */}
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.925rem", sm: "1rem" },
                  color: "#111827",
                  lineHeight: 1.35,
                }}
              >
                {headingText}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.815rem", sm: "0.85rem" },
                  color: "#4B5563",
                  lineHeight: 1.5,
                }}
              >
                {variant.description}
              </Typography>
            </Stack>
          </Stack>

          {/* Primary Referral Action Button */}
          <MUIButton
            component="a"
            href={variant.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            sx={{
              mt: 2.5,
              width: "100%",
              backgroundColor: "#0085FF",
              "&:hover": {
                backgroundColor: "#0073ea",
              },
              color: "#FFFFFF",
              borderRadius: "12px",
              py: "11px",
              px: 2,
              fontWeight: 700,
              fontSize: "0.95rem",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              textDecoration: "none !important",
              boxShadow: "0 2px 6px 0 rgba(0, 133, 255, 0.25)",
              "&:focus-visible": {
                outline: "2px solid #0085FF",
                outlineOffset: "2px",
              },
            }}
          >
            <FiSend size={18} style={{ display: "inline-block" }} />
            <span>{variant.cta}</span>
          </MUIButton>
        </Box>

        {/* Bottom Secondary Action (Close) */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
          <MUIButton
            onClick={onClose}
            variant="text"
            sx={{
              color: "#6B7280",
              fontWeight: 600,
              fontSize: "0.95rem",
              textTransform: "none",
              py: 0.5,
              px: 2,
              borderRadius: "8px",
              "&:hover": {
                color: "#111827",
                backgroundColor: "transparent",
              },
              "&:focus-visible": {
                outline: "2px solid #9CA3AF",
                outlineOffset: "2px",
              },
            }}
          >
            Close
          </MUIButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

PostSubmissionSuccessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sessionStartTime: PropTypes.string,
  audience: PropTypes.oneOf(["business-owner", "virtual-assistant"]),
  accountOwnerFirstName: PropTypes.string,
  firstName: PropTypes.string,
};

export default PostSubmissionSuccessModal;
